import { AfterViewInit, Directive, ElementRef, inject, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslationService } from 'app/core/internationalization/translation.service';

const DEFAULT_OTP_INPUT_LABEL_KEY = 'A11Y_OTP_INPUT.DIGIT_LABEL';

@Directive({
  selector: 'ng-otp-input[khA11yOtpInput]'
})
export class A11yOtpInputDirective implements AfterViewInit, OnChanges, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly translationService = inject(TranslationService);

  private mutationObserver?: MutationObserver;
  private viewInitialized = false;

  /** Optional override for feature-specific OTP wording. Defaults to the shared verification-code digit label. */
  @Input() public khA11yOtpInputLabelKey: string | null = null;

  public constructor() {
    this.translationService.translationsReady$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateInputLabels());
  }

  public ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.updateInputLabels();
    this.mutationObserver = new MutationObserver(() => this.updateInputLabels());
    this.mutationObserver.observe(this.elementRef.nativeElement, { childList: true, subtree: true });
  }

  public ngOnChanges(_changes: SimpleChanges): void {
    this.updateInputLabels();
  }

  public ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
  }

  private updateInputLabels(): void {
    if (!this.viewInitialized) {
      return;
    }

    const inputs = Array.from(this.elementRef.nativeElement.querySelectorAll<HTMLInputElement>('input.otp-input'));
    const total = inputs.length;
    const labelKey = this.khA11yOtpInputLabelKey || DEFAULT_OTP_INPUT_LABEL_KEY;

    inputs.forEach((input, index) => {
      const label = this.translationService.instant(labelKey, {
        position: index + 1,
        total
      });

      this.renderer.setAttribute(input, 'aria-label', label);
    });
  }
}
